import { DeductionListJson } from "@/api"
import { UserState } from "@/auth/user"
import { TableState } from "@/deduction_table/table_state"
import { currencyFormatter, get_display_name, to_reporting_amount } from "@/utils"
import { notifyChartFilterApplied } from "@/utils/filter_feedback"
import { BarList } from "@tremor/react"
import { useMemo } from "preact/compat"
import { capitalCase } from "change-case"

type TaskBreakdownData = {
  name: string
  value: number
  amount: number
  key?: string
  color?: string
}

const TASK_STATUS_COLORS: { [key: string]: string } = {
  "completed": "green",
  "pending": "yellow",
  "all-pending": "amber",
}

interface TaskBreakdownProps {
  data: DeductionListJson[]
}

export function TaskBreakdown({ data: rawData }: TaskBreakdownProps) {
  const user = UserState.use(u => u!)
  const currentUserName = get_display_name(user)

  let data = useMemo(() => {
    if (!rawData) return []

    let acc: Record<string, { count: number; amount: number; status: string; userName: string }> = {}
    let pendingCount = 0
    let pendingAmount = 0

    for (let row of rawData) {
      if (!row.task?.status || !row.task?.user_email) continue

      let status = row.task.status
      let userName = row.task.user_display_name ?? get_display_name(row.task.user_email)
      let amount = Math.abs(to_reporting_amount(row, row.deduction_amount))
      let key = `${status}: ${userName}`

      if (!acc[key]) {
        acc[key] = { count: 0, amount: 0, status, userName }
      }
      acc[key].count += 1
      acc[key].amount += amount

      if (status === "pending") {
        pendingCount += 1
        pendingAmount += amount
      }
    }

    let total = Object.values(acc).reduce((sum: number, v: { count: number }) => sum + v.count, 0)
    let nudge = total * 0.3

    let entries = Object.entries(acc)
      .map(([key, entry]) => ({ key, ...entry }))
      .sort((a, b) => {
        if (a.status !== b.status) return a.status === "pending" ? -1 : 1
        return a.userName.localeCompare(b.userName)
      })

    let results: TaskBreakdownData[] = []

    if (pendingCount > 0) {
      results.push({
        key: "pending",
        amount: pendingAmount,
        name: `All Pending (${pendingCount})`,
        value: pendingCount + nudge,
        color: TASK_STATUS_COLORS["all-pending"],
      })
    }

    for (let entry of entries) {
      let color =
        entry.userName === currentUserName && entry.status === "pending"
          ? "blue"
          : TASK_STATUS_COLORS[entry.status.toLowerCase()] || "slate"

      results.push({
        key: entry.key,
        amount: entry.amount,
        name: `${capitalCase(entry.status)}: ${capitalCase(entry.userName)} (${entry.count})`,
        value: entry.count + nudge,
        color,
      })
    }

    return results
  }, [rawData, currentUserName])

  return (
    <div class="flex">
      <BarList
        data={data}
        className="max-w-2xl grow"
        sortOrder="none"
        valueFormatter={(_: TaskBreakdownData) => ""}
        onValueChange={(item: TaskBreakdownData) => {
          TableState.set(s => ({
            ...s,
            search: "",
            earlyPay: false,
            columnFilters: [{ id: "task", value: item.key! }],
            pagination: { pageIndex: 0, pageSize: s.pagination.pageSize },
          }))
          notifyChartFilterApplied(`Showing deductions for tasks: ${item.name}.`)
        }}
      />
      <div class="xl:ml-6">
        {data.map((d: TaskBreakdownData) => (
          <div class="h-8 mb-1.5 items-center flex">{currencyFormatter(Math.abs(d.amount))}</div>
        ))}
      </div>
    </div>
  )
}
